import type { ObjectWorkspaceWorkbenchContext } from '@heidi/agents';
import type { LinkedContextItem } from '@heidi/agents/contracts';
import { composerDraftStorageKey } from '@heidi/agents/data';

import type { ObjectWorkspaceChatTab } from './object-workspace-project-chat-tab';

export interface ObjectWorkspaceChatHarnessState {
  tabs: ObjectWorkspaceChatTab[];
  activeTabId: string;
  announcement?: { id: number; text: string };
}

export type ObjectWorkspaceChatAnnounce = (
  current: ObjectWorkspaceChatHarnessState,
  next: Omit<ObjectWorkspaceChatHarnessState, 'announcement'>,
  text: string
) => ObjectWorkspaceChatHarnessState;

export const normalizedObjectWorkspaceTabTitle = (
  prompt: string,
  fallback: string
): string => prompt.replaceAll('\u200B', '').trim().replace(/\s+/g, ' ') || fallback;

export const objectWorkspaceComposerDraftStorageKey = (
  object: ObjectWorkspaceWorkbenchContext['object'],
  tabId: string
): string =>
  composerDraftStorageKey(`object-workspace:${object.kind}:${object.id}:${tabId}`);

export const emptyObjectWorkspaceChatTab = (
  id: string,
  title: string,
  kind: ObjectWorkspaceChatTab['kind'] = 'work'
): ObjectWorkspaceChatTab => ({
  id,
  title,
  kind,
  isDraftEmpty: true,
  hasQueuedTurns: false,
  isOpeningTurnInFlight: false,
  isTurnInFlight: false,
});

export const isObjectWorkspaceChatTabProtected = (tab: ObjectWorkspaceChatTab): boolean =>
  tab.isOpeningTurnInFlight ||
  tab.isTurnInFlight ||
  tab.hasQueuedTurns ||
  !tab.isDraftEmpty;

export const focusActiveWorkTab = () => {
  window.requestAnimationFrame(() => {
    document
      .querySelector<HTMLElement>(
        '[data-testid="object-workspace-work-tabs"] [role="tab"][data-state="active"]'
      )
      ?.focus();
  });
};

export const linkedContextForObject = ({
  id,
  kind,
  name,
  patientProfileId,
  sourceSessionId,
}: ObjectWorkspaceWorkbenchContext['object']): LinkedContextItem =>
  kind === 'visit'
    ? { type: 'session', sessionId: id, sessionName: name }
    : {
        type: 'patient',
        patientName: name,
        ...(patientProfileId
          ? { patientId: patientProfileId }
          : !sourceSessionId
            ? { patientId: id }
            : {}),
      };
